<script lang="ts">
  import type { PreviewBlock } from '$lib/model/types';
  import { projectStore } from '$lib/state/project.svelte';
  import { computeStatus } from '$lib/compute/recompute.svelte';
  import { pixelDimensions } from '$lib/compute/settings-utils';
  import { compositeLeafIntoCanvas } from '$lib/ops/compose';
  import { compositeRegMarks } from '$lib/ops/regMarks';
  import { computeTiles } from '$lib/compute/tiling';
  import { isExportableLeaf } from '$lib/model/tree';

  let canvas: HTMLCanvasElement | undefined = $state();
  let viewport: HTMLDivElement | undefined = $state();
  let showRegMarks = $state(true);
  let showTileGuides = $state(true);
  let viewMode = $state<'composite' | 'selected'>('composite');

  // Pan + zoom state. `scale` is uniform multiplier on the canvas CSS size,
  // `offsetX/Y` translate the canvas inside the viewport in screen pixels.
  let scale = $state(1);
  let offsetX = $state(0);
  let offsetY = $state(0);
  let isPanning = $state(false);
  let didFit = $state(false);

  const dims = $derived(pixelDimensions(projectStore.settings));
  const mirror = $derived(projectStore.settings.mirror_output === 'horizontal');
  const tiles = $derived(computeTiles(projectStore.settings));

  const layers = $derived.by(() => {
    const order = projectStore.layer_order;
    if (!order || order.length === 0) return [];
    const outputs = computeStatus.outputs;
    const byId = new Map<string, ImageData[]>(
      outputs.map((o) => [o.stepId, o.images]),
    );
    const result: { stepId: string; mask: ImageData; preview: PreviewBlock }[] = [];
    for (const id of order) {
      const step = projectStore.pipeline.steps[id];
      if (!step || !step.preview || !isExportableLeaf(step)) continue;
      if (!step.preview.enabled) continue;
      const imgs = byId.get(id);
      if (!imgs || imgs.length === 0) continue;
      const mask = imgs[0];
      if (!mask) continue;
      result.push({ stepId: id, mask, preview: step.preview });
    }
    return result;
  });

  const inputOutput = $derived.by(() => {
    const rootId = projectStore.pipeline.root_id;
    const found = computeStatus.outputs.find((o) => o.stepId === rootId);
    return found?.images[0] ?? null;
  });

  // Output of the currently-selected step. May have multiple images for
  // multi-output steps; the preview shows only the first.
  const selectedOutput = $derived.by(() => {
    const id = projectStore.selected_step_id;
    if (!id) return null;
    const found = computeStatus.outputs.find((o) => o.stepId === id);
    return found?.images[0] ?? null;
  });


  $effect(() => {
    if (!canvas) return;
    void layers;
    void inputOutput;
    void selectedOutput;
    void viewMode;
    void projectStore.settings.substrate_color;
    void projectStore.settings.reg_marks;
    void projectStore.settings.mark_size_in;
    void projectStore.settings.mark_margin_in;
    void projectStore.settings.mark_corners;
    void showRegMarks;
    for (const id of projectStore.layer_order) {
      const s = projectStore.pipeline.steps[id];
      if (s?.preview) {
        void s.preview.color;
        void s.preview.ink_mode;
        void s.preview.opacity;
        void s.preview.enabled;
      }
    }
    redraw();
  });

  // Re-fit when output dimensions change (initial render or target resize).
  $effect(() => {
    void dims.width;
    void dims.height;
    if (!didFit) {
      fit();
      didFit = true;
    }
  });

  // Note: we intentionally do NOT auto-switch view modes on step selection.
  // The composite view is the canonical "what the press will produce"; the
  // user explicitly opts into the step view via the toolbar toggle to
  // inspect an intermediate buffer when needed.

  function redraw() {
    if (!canvas) return;
    const w = dims.width;
    const h = dims.height;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (viewMode === 'selected' && selectedOutput) {
      // Show the selected step's output as a flat preview. Fill white first
      // so transparent borders (out-of-content letterbox) read as white.
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      const tmp = new OffscreenCanvas(
        selectedOutput.width,
        selectedOutput.height,
      );
      const tctx = tmp.getContext('2d');
      if (tctx) {
        tctx.putImageData(selectedOutput, 0, 0);
        ctx.drawImage(tmp, 0, 0, w, h);
      }
      return;
    }

    ctx.fillStyle = projectStore.settings.substrate_color;
    ctx.fillRect(0, 0, w, h);

    // When there are no exportable leaves yet, show whichever intermediate
    // buffer the user is most likely tuning: the currently-selected step's
    // output if available, else the rotated input. This lets the user see
    // greyscale / color-separation results before adding downstream layers.
    if (layers.length === 0) {
      const fallback = selectedOutput ?? inputOutput;
      if (fallback && fallback.width === w && fallback.height === h) {
        const sourceCanvas = new OffscreenCanvas(w, h);
        const sctx = sourceCanvas.getContext('2d');
        if (sctx) {
          sctx.putImageData(fallback, 0, 0);
          ctx.drawImage(sourceCanvas, 0, 0);
        }
      }
    }

    const offs = new OffscreenCanvas(w, h);
    const octx = offs.getContext('2d');
    if (!octx) return;
    octx.clearRect(0, 0, w, h);
    for (const layer of layers) {
      if (layer.mask.width === w && layer.mask.height === h) {
        compositeLeafIntoCanvas(octx, layer.mask, layer.preview);
      }
    }
    ctx.drawImage(offs, 0, 0);

    if (showRegMarks && projectStore.settings.reg_marks !== 'none') {
      const overlay = new ImageData(w, h);
      const baked = compositeRegMarks(overlay, projectStore.settings);
      const markCanvas = new OffscreenCanvas(w, h);
      const mctx = markCanvas.getContext('2d');
      if (mctx) {
        mctx.putImageData(baked, 0, 0);
        ctx.drawImage(markCanvas, 0, 0);
      }
    }
  }

  function fit() {
    if (!viewport) return;
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    if (vw === 0 || vh === 0 || dims.width === 0 || dims.height === 0) return;
    const margin = 32;
    const s = Math.min(
      (vw - margin) / dims.width,
      (vh - margin) / dims.height,
    );
    scale = Math.max(0.05, s);
    offsetX = (vw - dims.width * scale) / 2;
    offsetY = (vh - dims.height * scale) / 2;
  }

  function setZoom100() {
    if (!viewport) return;
    scale = 1;
    offsetX = (viewport.clientWidth - dims.width) / 2;
    offsetY = (viewport.clientHeight - dims.height) / 2;
  }

  function onWheel(e: WheelEvent) {
    if (!viewport) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const rect = viewport.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // World coords of cursor before zoom
    const wx = (cx - offsetX) / scale;
    const wy = (cy - offsetY) / scale;
    const next = Math.max(0.05, Math.min(20, scale * factor));
    scale = next;
    offsetX = cx - wx * scale;
    offsetY = cy - wy * scale;
  }

  let panStartX = 0;
  let panStartY = 0;
  let panInitOffX = 0;
  let panInitOffY = 0;

  function onMouseDown(e: MouseEvent) {
    if (e.button !== 0) return;
    isPanning = true;
    panStartX = e.clientX;
    panStartY = e.clientY;
    panInitOffX = offsetX;
    panInitOffY = offsetY;
  }
  function onMouseMove(e: MouseEvent) {
    if (!isPanning) return;
    offsetX = panInitOffX + (e.clientX - panStartX);
    offsetY = panInitOffY + (e.clientY - panStartY);
  }
  function onMouseUp() {
    isPanning = false;
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === '0') fit();
    else if (e.key === '1') setZoom100();
    else if (e.key === '+' || e.key === '=') scale = Math.min(20, scale * 1.2);
    else if (e.key === '-') scale = Math.max(0.05, scale / 1.2);
  }
</script>

<svelte:window onmouseup={onMouseUp} onkeydown={onKey} />

<div class="preview">
  <div class="toolbar">
    <button onclick={fit} title="Fit to view (0)">⤢ fit</button>
    <button onclick={setZoom100} title="100% (1)">1:1</button>
    <span class="zoom">{Math.round(scale * 100)}%</span>
    <span class="sep"></span>
    <div class="view-mode" role="group" aria-label="view mode">
      <button
        class:active={viewMode === 'composite'}
        onclick={() => (viewMode = 'composite')}
        title="Show the layered composite">composite</button
      >
      <button
        class:active={viewMode === 'selected'}
        onclick={() => (viewMode = 'selected')}
        disabled={!selectedOutput}
        title="Show the selected step's output">step</button
      >
    </div>
    <span class="sep"></span>
    {#if viewMode === 'composite'}
      <label class="check">
        <input type="checkbox" bind:checked={showRegMarks} />
        reg marks
      </label>
      {#if tiles.tiles.length > 1}
        <label class="check">
          <input type="checkbox" bind:checked={showTileGuides} />
          tile guides
        </label>
      {/if}
    {/if}
    <span class="spacer"></span>
    <span class="dims">
      {#if viewMode === 'selected' && selectedOutput}
        {selectedOutput.width}×{selectedOutput.height}px (step output)
      {:else}
        {dims.width}×{dims.height}px
        {#if tiles.tiles.length > 1}
          · {tiles.cols}×{tiles.rows} tiles
        {/if}
      {/if}
    </span>
  </div>

  <div
    class="viewport"
    bind:this={viewport}
    onwheel={onWheel}
    onmousedown={onMouseDown}
    onmousemove={onMouseMove}
    role="presentation"
  >
    <div
      class="canvas-wrap"
      class:mirror
      style="transform: translate({offsetX}px, {offsetY}px) scale({scale});"
    >
      <canvas bind:this={canvas} class:panning={isPanning}></canvas>
      {#if showTileGuides && tiles.tiles.length > 1}
        <div class="tile-overlay">
          {#each tiles.tiles as t (t.row + '-' + t.col)}
            <div
              class="tile-rect"
              style="left:{t.x_px}px; top:{t.y_px}px; width:{t.width_px}px; height:{t.height_px}px;"
            >
              <span class="tag">{t.row},{t.col}</span>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .preview {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }
  .toolbar {
    display: flex;
    align-items: center;
    gap: var(--gap-m);
    padding: 6px var(--gap-m);
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    font-size: 12px;
  }
  .toolbar button {
    font-size: 11px;
    padding: 4px 8px;
  }
  .zoom {
    font-family: var(--font-mono);
    color: var(--text-dim);
    width: 40px;
    text-align: right;
  }
  .sep {
    width: 1px;
    height: 18px;
    background: var(--border);
  }
  .spacer {
    flex: 1;
  }
  .dims {
    font-family: var(--font-mono);
    color: var(--text-dim);
  }
  .check {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .view-mode {
    display: inline-flex;
    border: 1px solid var(--border);
    border-radius: 3px;
    overflow: hidden;
  }
  .view-mode button {
    font-size: 11px;
    padding: 4px 8px;
    background: transparent;
    border: none;
    border-radius: 0;
    color: var(--text-dim);
  }
  .view-mode button.active {
    background: var(--accent-dim);
    color: var(--bg);
  }
  .view-mode button[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .viewport {
    flex: 1;
    overflow: hidden;
    position: relative;
    background:
      linear-gradient(45deg, #181818 25%, transparent 25%),
      linear-gradient(-45deg, #181818 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #181818 75%),
      linear-gradient(-45deg, transparent 75%, #181818 75%);
    background-size: 16px 16px;
    background-position:
      0 0,
      0 8px,
      8px -8px,
      -8px 0;
    background-color: #111;
    cursor: grab;
    user-select: none;
  }
  .viewport:active {
    cursor: grabbing;
  }
  .canvas-wrap {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
    will-change: transform;
  }
  .canvas-wrap.mirror canvas,
  .canvas-wrap.mirror .tile-overlay {
    transform: scaleX(-1);
    transform-origin: top left;
  }
  canvas {
    display: block;
    background: var(--bg);
    image-rendering: pixelated;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
  }
  canvas.panning {
    pointer-events: none;
  }
  .tile-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .tile-rect {
    position: absolute;
    border: 1px dashed var(--accent);
    box-sizing: border-box;
  }
  .tag {
    position: absolute;
    top: 4px;
    left: 4px;
    background: var(--accent);
    color: var(--bg);
    font-family: var(--font-mono);
    font-size: 10px;
    padding: 1px 4px;
    border-radius: 2px;
  }
</style>
